// client/src/intro-exit/VariableIntroduction.jsx

/**
 * This file renders the structural loan analysis metric overview and baseline metrics table.
 * It conditions visual rows dynamically based on assigned player role variables (Expert status, AI eligibility)
 * and references centralized mathematical values directly from the experimental configuration.
 */


import React from "react";
// PREDICTION, RiskBandTable and historicalSamples are referenced only from the two
// commented-out blocks below. Kept so those blocks restore by uncommenting alone --
// do not let a linter strip them.
import { historicalSamples, featureAverages, PREDICTION } from "../cases.js";
import RiskBandTable from "../RiskBandTable.jsx";
import dtiGraph from "./Debt-to-Income_graph.png";
import creditLimitGraph from "./Total_Credit_Limit_graph.png";
import creditScoreGraph from "./Credit_Score_graph.png";
import revolveGraph from "./Revolving_Utilization_graph.png";
import aiGraph from "./AI_Risk_Score_graph.png";
import { variable_theme } from "../theme.js";

// ==========================================
// 1. Variable Introduction Sub-Component View
// ==========================================
// Averages and the default rate either side of them, from `featureAverages`.
// Rounded here so the instruction prose and the numbers it quotes cannot drift.
const fmtPct = (v) => `${Math.round(v)}%`;
const DTI_AVG = fmtPct(featureAverages.dti.mean);
const DTI_GOOD = Math.round(featureAverages.dti.defaultBelowMean);
const DTI_BAD = Math.round(featureAverages.dti.defaultAboveMean);

// For the credit score the safe side is ABOVE average; for revolving utilization
// it is below, so the two roles quote the two sides in opposite order.
const PRIVATE_AVG_E = Math.round(featureAverages.fico.mean);
const PRIVATE_GOOD_E = Math.round(featureAverages.fico.defaultAboveMean);
const PRIVATE_BAD_E = Math.round(featureAverages.fico.defaultBelowMean);

const PRIVATE_AVG_N = fmtPct(featureAverages.revol_util.mean);
const PRIVATE_GOOD_N = Math.round(featureAverages.revol_util.defaultBelowMean);
const PRIVATE_BAD_N = Math.round(featureAverages.revol_util.defaultAboveMean);

export default function VariableIntroduction({ player }) {
  const isExpert = player.get("isExpert");
  const hasAI = player.get("hasAI");

  // ==========================================
  // 2. Main Return Component Render
  // ==========================================

  const tPublic = variable_theme.public;
  const tPrivate = variable_theme.private;
  const tAI = variable_theme.ai;

  return (<div className="space-y-3 bg-white p-0 font-sans">
      {/* SECTION: UNDERSTANDING THE LOAN INFO */}
      <div className="space-y-2">
        <h3 className="app-subsection-title text-gray-900 border-l-2 border-gray-900 pl-2">
          Understanding the Loan Info</h3>
        <p className="app-lead text-gray-900">You will be shown the {3 + (hasAI ? 1 : 0)} variables below in your screen for each borrower:</p>

        {/* TASK 15: PLACED BETWEEN THE TEXT LINE AND THE TABLE */}
        <ul className="app-body list-disc list-inside space-y-1 text-gray-800 rounded-xl px-4">
          <li>
            <span className={`font-bold ${tPublic.textColor}`}>Debt-to-Income</span> and <span className={`font-bold ${tPublic.textColor}`}>Total Credit Limit</span> are <strong>public features</strong> that are visible to all players.
          </li>
          <li>
            <span className={`font-bold ${tPrivate.textColor}`}>{isExpert ? "Credit Score" : "Revolving Utilization"}</span> is your <strong>private feature</strong> unique to you.
          </li>
          {hasAI && (
            <li>
              <span className={`font-bold ${tAI.textColor}`}>AI Risk Recommendation</span> does not have access to your private information.
            </li>
          )}
        </ul>

        {/* How to read the plots in the last column. Direction alone ("higher is
            safer") does not tell a participant how much weight a feature deserves,
            which is the judgement the hard cases turn on -- so name the slope as
            the thing to compare, and state where this participant's own private
            feature lands relative to a public one they can see. */}
        <p className="app-lead text-gray-900">
          Below you see the relationship between the chance of default and each of these
          variables. <strong>The steeper the line, the more helpful that variable is for
          predicting default.</strong>
        </p>

        {/* Which variable is worth the most, said twice: once as a ranking and once as
            two numbers a reader can feel. The ranking alone was not landing -- experts
            reported afterwards that they leaned on the credit score, while their accuracy
            on the cases it decides said they could not turn a value into a judgement.
            The averages supply the reference point that was missing, and the counts are
            out of 100 because that form is read correctly far more often than the same
            figure written as a percentage. */}
        <div className={`rounded-xl border p-4 ${tPrivate.borderColor} ${tPrivate.bgColor}`}>
          <p className="app-callout text-gray-900">
            Your private{" "}
            <span className={`font-bold ${tPrivate.textColor}`}>
              {isExpert ? "Credit Score" : "Revolving Utilization"}
            </span>{" "}
            is <strong>{isExpert ? "much more" : "less"}</strong> informative than the public{" "}
            <span className={`font-bold ${tPublic.textColor}`}>Debt-to-Income</span> ratio.
          </p>

          <p className="app-body text-gray-800 mt-2">
            Here is what that means in practice. Among past borrowers:
          </p>

          <ul className="app-body list-disc list-inside space-y-1 text-gray-800 mt-1">
            <li>
              <span className={`font-bold ${tPrivate.textColor}`}>
                {isExpert ? "Credit Score" : "Revolving Utilization"}
              </span>{" "}
              — the average is{" "}
              <strong>{isExpert ? PRIVATE_AVG_E : PRIVATE_AVG_N}</strong>. Of those{" "}
              <strong>{isExpert ? "above" : "below"}</strong> that average,{" "}
              <strong className="text-red-700">
                {isExpert ? PRIVATE_GOOD_E : PRIVATE_GOOD_N} out of 100
              </strong>{" "}
              defaulted. Of those{" "}
              <strong>{isExpert ? "below" : "above"}</strong> it,{" "}
              <strong className="text-red-700">
                {isExpert ? PRIVATE_BAD_E : PRIVATE_BAD_N} out of 100
              </strong>{" "}
              defaulted.
            </li>
            <li>
              <span className={`font-bold ${tPublic.textColor}`}>Debt-to-Income</span> — the
              average is <strong>{DTI_AVG}</strong>. Of those <strong>below</strong> that
              average, <strong className="text-red-700">{DTI_GOOD} out of 100</strong> defaulted.
              Of those <strong>above</strong> it,{" "}
              <strong className="text-red-700">{DTI_BAD} out of 100</strong> defaulted.
            </li>
          </ul>

          <p className="app-body text-gray-800 mt-2">
            {isExpert ? (
              <>
                <strong>Notice the difference. When Credit Score is high or low, it is
                telling you far more than when the public features are high or low.</strong>
              </>
            ) : (
              <>
                <strong>Notice the difference. When Debt-to-Income is high or low, it is telling you 
                slightly more than when the private feature is high or low.</strong>
              </>
            )}
          </p>
        </div>

        <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
          <table className="app-table-cell min-w-full divide-y divide-gray-200 bg-white text-left text-gray-900">
            <thead className="app-table-header bg-gray-900 text-white">
              <tr><th className="px-4 py-3 text-center ">Variable Name</th>
                <th className="px-4 py-3 text-left">General Trend</th>
                <th className="px-4 py-3 w-[32%] text-center">What It Means</th>
                <th className="px-4 py-3 w-[34%] text-center">Visual Representation</th>
                </tr></thead>
            <tbody className="divide-y divide-gray-200 text-gray-700">
              {/* Public Variable 1 - Universal */}
              <tr className={`${tPublic.bgColor} ${tPublic.borderColor}`}><td className={`px-4 py-3 font-bold text-center ${tPublic.textColor}`}>Debt-to-Income</td>
                <td className="px-4 py-3 text-center"> <div className="font-bold mt-0.5 flex items-center justify-center gap-0.5 text-slate-700">
                    <span>↓</span> Lower is Safer </div></td>
                <td className="px-4 py-3 text-gray-600 justify-center">Shows total debt compared to income. Higher percentages mean they are heavily in debt and riskier.</td>
                <td className="px-2 py-2 text-center"> <img src={dtiGraph} alt="DTI Trend" className="w-full h-auto object-contain " /></td></tr>
              
              {/* Public Variable 2 - Universal */}
              <tr className={`${tPublic.bgColor} ${tPublic.borderColor}`}><td className={`px-4 py-3 font-bold text-center ${tPublic.textColor}`}>Total Credit <br /> Limit</td>
                <td className="px-4 py-3 text-center"><div className="font-bold mt-0.5 flex items-center justify-center gap-0.5 text-slate-700">
                    <span>↑</span> Higher is Safer</div></td>
                <td className="px-4 py-3 text-gray-600 justify-center">The total approved revolving credit limits assigned to the borrower. Higher limits reflect favorable historical credibility.</td>
                <td className="px-2 py-2 text-center"><img src={creditLimitGraph} alt="Credit Limit Trend" className="w-full h-auto object-contain" /></td></tr>

              {/* Variable 3 for Non-Experts ONLY */}
              {!isExpert && (<tr className={`${tPrivate.bgColor} ${tPrivate.borderColor}`}>
                <td className={`px-4 py-3 font-bold text-center ${tPrivate.textColor}`}>Revolving Utilization <br />(Private Information)</td>
                <td className="px-4 py-3 text-center"><div className="font-bold mt-0.5 flex items-center justify-center gap-0.5 text-slate-700">
                      <span>↓</span> Lower is Safer</div></td>
                  <td className="px-4 py-3 text-gray-600 justify-center">This information is only available to you. It shows the ratio of active balances to credit limits. Higher percentages imply higher financial strain.</td>
                  <td className="px-2 py-2 text-center"><img src={revolveGraph} alt="Trend" className="w-full h-auto object-contain" /></td></tr>
              )}

              {/* Variable 4 for Experts ONLY */}
              {isExpert && (<tr className={`${tPrivate.bgColor} ${tPrivate.borderColor}`}>
                  <td className={`px-4 py-3 font-bold text-center ${tPrivate.textColor}`}>Credit Score <br />(Private Information)</td>
                  <td className="px-4 py-3 text-center"><div className="font-bold mt-0.5 flex items-center justify-center gap-0.5 text-slate-700">
                      <span>↑</span> Higher is Safer</div></td>
                  <td className="px-4 py-3 text-gray-600 justify-center">This information is only available to you. Borrowers with high credit scores are much more likely to pay back their loans.</td>
                  <td className="px-2 py-2 text-center"><img src={creditScoreGraph} alt="Trend" className="w-full h-auto object-contain" /></td></tr>
                  )}

              {/* Supplementary AI Engine Entry - Displayed for AI treatments only */}
              {hasAI && (<tr className={`${tAI.bgColor} ${tAI.borderColor}`}>
                  <td className={`px-4 py-3 font-bold text-center ${tAI.textColor}`}>AI Risk<br/>Recommendation</td>
                  <td className="px-4 py-3 text-center"> <div className="font-bold mt-0.5 flex items-center justify-center gap-0.5 text-slate-700">
                      <span>↓</span> Lower is Safer</div></td>
                  <td className="px-4 py-3 text-gray-600 justify-center">Statistical forecasting model prediction evaluated based on the <strong>2 public features</strong> (Debt-to-Income and Total Credit Limit). Higher percentages highlight an increased threat of default.</td>
                  <td className="px-2 py-2 text-center"><img src={aiGraph} alt="Trend" className="w-full h-auto object-contain" /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      
      {/* SECTION: HISTORICAL DEFAULT RATES BY RISK BAND -- COMMENTED OUT.
          Superseded by the above/below-average callout higher up the page, which
          makes the same point (how sharply each variable sorts borrowers) using one
          reference point per feature rather than two thresholds, and in counts out
          of 100. Two tables of default rates on one page was more arithmetic than
          the page needs, and the callout is the version tied to the averages the
          prediction screen's percentile lines refer to.

          Kept rather than deleted: RiskBandTable is still live and still shared, so
          restoring this is uncommenting a block.

      <div className="space-y-2 pt-2">
        <h3 className="app-subsection-title text-gray-900 border-l-2 border-gray-900 pl-2">
          Historical Default Rates
        </h3>
        <p className="app-body text-gray-700">
          Here is how often past loans ended in {PREDICTION.DEFAULT}, among loans with the
          lowest and the highest values of each of your {3 + (hasAI ? 1 : 0)} variables:
        </p>
        <RiskBandTable isExpert={isExpert} hasAI={hasAI} />
      </div>
      */}
      
      {/* SECTION: TRUE LOAN OUTCOMES -- replaced by the averages table above.
          Kept commented rather than deleted so it can be swapped back in. */}
      {/* <div className="space-y-2 pt-2">
        <h3 className="app-subsection-title text-gray-900 border-l-2 border-gray-900 pl-2">
          True Loan Outcomes
        </h3>
        <p className="app-body text-gray-700">
          Here are real examples from past records to give you an idea of how these numbers work together:
        </p>

        <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm bg-white">
          <table className="app-table-cell min-w-full divide-y divide-gray-200 bg-white text-left font-sans">
            <thead className="app-table-header bg-gray-100 text-gray-700">
              <tr>
                <th className={`px-4 py-2 text-center`}>Example <br /> Loan</th>
                <th className={`px-3 py-2 text-right ${tPublic.textColor}`}>Debt-to-<span className="block">Income</span></th>
                <th className={`px-4 py-2 text-right ${tPublic.textColor}`}>Total Revolving <span className="block">Credit Limit</span></th>

                {!isExpert && <th className={`px-4 py-2 text-right ${tPrivate.textColor}`}>Revolving Utilization <span className="block">(Private Info)</span></th>}
                {isExpert && <th className={`px-4 py-2 text-right ${tPrivate.textColor} bg-purple-50/10`}>Credit Score <span className="block">(Private Info)</span></th>}
                {hasAI && <th className={`px-5 py-2 ${tAI.textColor} bg-purple-50/40 text-right`}>AI Risk <span className="block">Forecast</span></th>}
                
                <th className={`px-4 py-2 text-center`}>What <span className="block">Happened?</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-600 font-sans tabular-nums">
              {historicalSamples.map((record, index) => (
                <tr key={record.id} className={index % 2 === 1 ? "bg-gray-50/50" : ""}>
                  <td className={`px-4 py-1.5 text-gray-900 text-center ${tPublic.textColor}`}>{record.id}</td>
                  <td className={`px-4 py-1.5 text-right font-bold ${tPublic.textColor} tabular-nums`}>{record.dti.toFixed(1)}%</td>
                  <td className={`px-4 py-1.5 text-right font-bold ${tPublic.textColor} tabular-nums`}>${(record.total_rev_hi_lim).toLocaleString()}</td>

                  {!isExpert && <td className={`px-4 py-1.5 text-right font-bold ${tPrivate.textColor} tabular-nums`}>{record.revol_util}%</td>}
                  {isExpert && <td className={`px-4 py-1.5 text-right font-bold ${tPrivate.textColor} bg-purple-50/10 tabular-nums`}>{(record.fico)}</td>}
                  {hasAI && <td className={`px-4 py-1.5 font-bold ${tAI.textColor} bg-purple-50/10 text-right tabular-nums`}>{(record.p_ai * 100).toFixed(1)}%</td>}
                  
                  <td className={`px-4 py-1.5 font-sans font-bold text-center ${
                    record.outcome === "Repaid" ? "text-emerald-600" : "text-rose-600"
                  }`}>
                    {record.outcome.toUpperCase()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}


    </div>
  );
}
